import { FC, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useScoringStore, MAX_ACTIVE_PROFILES } from "@/store/scoringStore";
import {
  useCanManageScoring,
  MANAGE_SCORING_DISABLED_MESSAGE,
} from "@/hooks/useCanManageScoring";
import { profileColor } from "@/lib/scoring/profileColors";
import type { ScoringProfile } from "@/lib/scoring/types";

/** Two-letter code from the first letters of the first two meaningful words. */
function initials(name: string): string {
  const words = name.replace(/[—–-]/g, " ").split(/\s+/).filter(Boolean);
  const letters =
    (words[0]?.[0] ?? "") + (words[1]?.[0] ?? words[0]?.[1] ?? "");
  return letters.toUpperCase();
}

/**
 * Which view the pills are shown in, so they can label profile roles:
 * - "matrix": the focused profile is marked "Scoring".
 * - "compare": the focused profile is "A" and the compare profile is "B".
 * - "list": no role labels.
 */
export type PillsMode = "list" | "matrix" | "compare";

interface ScoringProfilePillsProps {
  onCreateNew: () => void;
  /** Open the profile editor for an existing profile. */
  onEditProfile: (profile: ScoringProfile) => void;
  mode?: PillsMode;
}

export const ScoringProfilePills: FC<ScoringProfilePillsProps> = ({
  onCreateNew,
  onEditProfile,
  mode = "list",
}) => {
  const canManage = useCanManageScoring();
  const profiles = useScoringStore((s) => s.profiles);
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const focusedProfileId = useScoringStore((s) => s.focusedProfileId);
  const compareProfileId = useScoringStore((s) => s.compareProfileId);
  const activateProfile = useScoringStore((s) => s.activateProfile);
  const deactivateProfile = useScoringStore((s) => s.deactivateProfile);
  const setFocusedProfile = useScoringStore((s) => s.setFocusedProfile);

  const roleFor = (id: number): string | null => {
    if (mode === "compare") {
      if (id === focusedProfileId) return "A";
      if (id === compareProfileId) return "B";
      return null;
    }
    if (mode === "matrix" && id === focusedProfileId) return "Scoring";
    return null;
  };

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const activeProfiles = activeProfileIds
    .map((id) => profiles.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => p != null);
  const inactiveProfiles = profiles.filter(
    (p) => !activeProfileIds.includes(p.id)
  );
  const atMax = activeProfileIds.length >= MAX_ACTIVE_PROFILES;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      {activeProfiles.map((profile, index) => {
        const color = profileColor(index);
        const focused = profile.id === focusedProfileId;
        const role = roleFor(profile.id);
        return (
          <Chip
            key={profile.id}
            data-testid={`scoring-profile-pill-${profile.id}`}
            onClick={() => setFocusedProfile(profile.id)}
            onDelete={() => deactivateProfile(profile.id)}
            avatar={
              <Avatar
                sx={{
                  bgcolor: `${color} !important`,
                  color: "#fff !important",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {initials(profile.name)}
              </Avatar>
            }
            label={
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {role && (
                  <Box
                    component="span"
                    data-testid={`scoring-profile-role-${profile.id}`}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      px: 0.5,
                      borderRadius: 0.5,
                      color: "#fff",
                      bgcolor: color,
                      textTransform: "uppercase",
                    }}
                  >
                    {role}
                  </Box>
                )}
                {profile.name}
                {canManage && (
                  <Box
                    component="span"
                    role="button"
                    aria-label={`Edit ${profile.name}`}
                    data-testid={`scoring-edit-profile-btn-${profile.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditProfile(profile);
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      color: "text.secondary",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                  </Box>
                )}
              </Box>
            }
            variant="outlined"
            sx={{
              maxWidth: 300,
              bgcolor: "background.paper",
              borderColor: focused ? color : "divider",
              borderWidth: focused ? 2 : 1,
              fontWeight: focused ? 600 : 400,
            }}
          />
        );
      })}

      {activeProfiles.length === 0 ? (
        // No profile applied yet — scoring is a primary action on this page, so
        // surface it as a prominent CTA rather than a faint text button.
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<AutoAwesomeIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          data-testid="scoring-add-profile-btn"
          sx={{ textTransform: "none", flexShrink: 0, fontWeight: 600 }}
        >
          Add scoring profile
        </Button>
      ) : (
        // A profile is already applied — adding another (for matrix compare) is
        // secondary, so keep it to a compact icon button.
        !atMax && (
          <Tooltip title="Add another scoring profile">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              data-testid="scoring-add-profile-btn"
              sx={{ flexShrink: 0 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}
      >
        <Tooltip
          title={canManage ? "" : MANAGE_SCORING_DISABLED_MESSAGE}
          placement="right"
        >
          {/* span keeps the tooltip working while the item is disabled */}
          <span>
            <MenuItem
              data-testid="scoring-create-profile-btn"
              disabled={!canManage}
              onClick={() => {
                setMenuAnchor(null);
                onCreateNew();
              }}
              sx={{
                gap: 1,
                py: 1,
                color: "primary.main",
                fontWeight: 600,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <AddIcon fontSize="small" />
              Create scoring profile
            </MenuItem>
          </span>
        </Tooltip>
        {inactiveProfiles.length > 0 && <Divider />}
        {inactiveProfiles.map((profile) => (
          // Not disabled at max: activation is gated in onClick, but the edit
          // action must stay reachable so every profile can be edited from here.
          <MenuItem
            key={profile.id}
            onClick={() => {
              if (atMax) return;
              activateProfile(profile.id);
              setMenuAnchor(null);
            }}
            sx={{ gap: 1, pr: 1 }}
          >
            <ListItemText
              primary={
                <Box
                  component="span"
                  sx={{ color: atMax ? "text.disabled" : "inherit" }}
                >
                  {profile.name}
                </Box>
              }
            />
            <Tooltip title="Edit profile">
              <IconButton
                size="small"
                edge="end"
                aria-label={`Edit ${profile.name}`}
                data-testid={`scoring-edit-profile-btn-${profile.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(null);
                  onEditProfile(profile);
                }}
                sx={{ ml: "auto" }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
